import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, PlayCircle, Radio, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { autoCloseAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

// Mirrors models_attendance_integrity close reasons.
const REASON = {
  shift_end: { label: 'Shift ended', tone: 'bg-blue-500/15 text-blue-600' },
  silence: { label: 'Device went quiet', tone: 'bg-amber-500/15 text-amber-600' },
  geofence_exit: { label: 'Left the site', tone: 'bg-purple-500/15 text-purple-600' },
  location_spoofing: { label: 'Faked location', tone: 'bg-red-500/15 text-red-600' },
};

const time = (value) => (value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const stamp = (value) => (value ? new Date(value).toLocaleString() : '—');

/// What the system closed and why.
///
/// An enforced policy nobody can inspect is a support queue: the first thing an
/// employee asks is "why does it say I left at four", and this is the screen that
/// answers it — with the beacons the decision was actually made from.
const AutoClosedPunches = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [preview, setPreview] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [beacons, setBeacons] = useState([]);
  const [loadingBeacons, setLoadingBeacons] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await autoCloseAPI.list();
      setItems(res?.data?.items || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load auto-closed punches'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dryRun = async () => {
    try {
      setSweeping(true);
      const res = await autoCloseAPI.runSweep(true);
      setPreview(res?.data || null);
      toast.success(`${res?.data?.count ?? 0} punch(es) would close`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not run the preview'));
    } finally {
      setSweeping(false);
    }
  };

  const openBeacons = async (row) => {
    if (openId === row.attendance_id) { setOpenId(null); return; }
    setOpenId(row.attendance_id);
    try {
      setLoadingBeacons(true);
      const res = await autoCloseAPI.beacons(row.attendance_id);
      setBeacons(res?.data?.items || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not load the beacon timeline'));
      setBeacons([]);
    } finally {
      setLoadingBeacons(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-foreground text-base">Automatically closed punches</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Shifts the system closed because nobody did. Each one shortened a working
                day, so each one can be corrected through regularization — and the beacons
                behind the decision are here to settle the argument.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              {/* Dry run before the flag goes on for real: the whole rollout plan. */}
              <Button variant="outline" size="sm" onClick={dryRun} disabled={sweeping}>
                {sweeping ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-1" /> Preview sweep</>}
              </Button>
              <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          {preview && (
            <div className="rounded-lg border border-dashed border-border p-3 text-xs">
              <p className="text-foreground font-medium">
                Preview only — nothing was changed. {preview.count} punch(es) would close now.
              </p>
              {(preview.items || []).map((p) => (
                <p key={`${p.attendance_id}`} className="text-muted-foreground mt-1">
                  {p.employee_id} → {stamp(p.close_at)} ({REASON[p.reason]?.label || p.reason}, evidence: {p.evidence})
                </p>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              Nothing has been closed automatically. That is the expected state until the
              feature is switched on in Security settings.
            </p>
          ) : items.map((row) => (
            <div key={row.attendance_id} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground">{row.employee_id}</p>
                    <Badge className={REASON[row.reason]?.tone || 'bg-muted text-muted-foreground'}>
                      {REASON[row.reason]?.label || row.reason || 'closed'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {row.date} · in {time(row.check_in_time)} → out {time(row.check_out_time)}
                    {row.total_hours != null ? ` · ${Number(row.total_hours).toFixed(2)} h` : ''}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openBeacons(row)}>
                  <Radio className="h-4 w-4 mr-1" />
                  {openId === row.attendance_id ? 'Hide' : 'Evidence'}
                </Button>
              </div>

              {openId === row.attendance_id && (
                <div className="mt-3 border-t border-border/60 pt-2">
                  {loadingBeacons ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading beacons…
                    </div>
                  ) : beacons.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1">
                      No beacons recorded — this was closed on the roster alone.
                    </p>
                  ) : (
                    <div className="max-h-56 overflow-y-auto space-y-1">
                      {beacons.map((b, index) => (
                        <p key={`${b.captured_at}-${index}`} className="text-[11px] text-muted-foreground">
                          {time(b.captured_at)}
                          {' · '}
                          {b.inside_geofence === true ? 'inside' : b.inside_geofence === false ? 'outside' : 'location unknown'}
                          {b.accuracy_m != null ? ` (±${Math.round(b.accuracy_m)} m)` : ''}
                          {b.location_mocked ? ' · MOCK LOCATION' : ''}
                          {b.monitor_mode ? ` · ${b.monitor_mode}` : ''}
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-2 whitespace-pre-wrap">{row.notes}</p>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AutoClosedPunches;
